import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Button, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import GameScreenShell from "@components/Home/Games/GameScreenShell";
import SessionSummary, { SessionSummaryStats } from "@components/Home/Games/SessionSummary";
import type { GameSessionGame } from "@models/game";
import { GAME_IDS } from "@models/game";
import {
  useStartSessionMutation,
  useSubmitRoundMutation,
  useCompleteSessionMutation,
} from "@store/gameApi";
import { RootState } from "@store/store";
import {
  gameContinueButtonSx,
  gameSecondaryButtonSx,
} from "@components/Home/Games/gameUi";

export interface SequenceRecallGuidedProps {
  guidedSessionId?: string;
  guidedCurrentGame?: GameSessionGame;
  guidedGameIndex?: number;
  guidedTotalGames?: number;
  onGuidedGameComplete?: () => void;
}

const GRID_ROWS = 3;
const GRID_COLS = 4;
const CELL_COUNT = GRID_ROWS * GRID_COLS;
const CELL_SIZE_DESKTOP = 88;
const GRID_GAP_DESKTOP = 12;
/** Spec: minimum 80px × 80px; 72×72 with 8px gap on small screens allowed — use 80 for consistency */
const CELL_SIZE_MOBILE = 84;
const GRID_GAP_MOBILE = 6;
const SHOW_ITEM_MS = 800;
const GAP_BETWEEN_MS = 400;
const MIN_SEQUENCE = 2;
const MAX_SEQUENCE = 12;
const INITIAL_SEQUENCE_LENGTH = 3;
const ROUNDS_FOR_ACCURACY = 5;
const ACCURACY_UP_THRESHOLD = 0.85;
const ACCURACY_DOWN_THRESHOLD = 0.6;
const ROUNDS_PER_SESSION = 3;
const SESSION_START_DEDUPE_MS = 2000;

/** Dedupes session/start across Strict Mode double-mount. */
let lastSessionStartAt = 0;

type Phase = "idle" | "showing" | "recall" | "feedback";

/** Simple seeded pseudo-random in [0, 1) for deterministic sequences when contentSeed is provided */
function seededRandom(seed: string): () => number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
}

function generateSequence(length: number, contentSeed?: string): number[] {
  const indices = Array.from({ length: CELL_COUNT }, (_, i) => i);
  const out: number[] = [];
  const rng = contentSeed ? seededRandom(contentSeed) : null;
  for (let i = 0; i < length; i++) {
    const n = indices.length;
    const j = rng ? Math.floor(rng() * n) : Math.floor(Math.random() * n);
    out.push(indices[j]);
    indices.splice(j, 1);
  }
  return out;
}

interface FreePlayLocationState {
  freePlaySessionId?: string;
  freePlayGames?: GameSessionGame[];
}

const SequenceRecall: FC<SequenceRecallGuidedProps> = ({
  guidedSessionId,
  guidedCurrentGame,
  guidedGameIndex = 0,
  guidedTotalGames = 1,
  onGuidedGameComplete,
}) => {
  const location = useLocation();
  const freePlayState = location.state as FreePlayLocationState | null;
  const freePlaySessionId = freePlayState?.freePlaySessionId;
  const freePlayGame = freePlayState?.freePlayGames?.[0];

  const isGuided = Boolean(guidedSessionId && guidedCurrentGame);
  const isFreePlayFromLibrary = Boolean(freePlaySessionId && freePlayGame);
  const effectiveSessionId = guidedSessionId ?? freePlaySessionId ?? null;
  const effectiveGame = guidedCurrentGame ?? freePlayGame;

  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fallbackCellSize = isMobile
    ? CELL_SIZE_MOBILE
    : CELL_SIZE_DESKTOP;
  const gridGap = isMobile ? GRID_GAP_MOBILE : GRID_GAP_DESKTOP;

  const contentBoxRef = useRef<HTMLDivElement | null>(null);
  const [contentBoxSize, setContentBoxSize] = useState({
    width: 0,
    height: 0,
  });

  // Measure available play area so we can maximize the pad squares without
  // overflowing into the shell footer (score).
  useEffect(() => {
    const el = contentBoxRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const cr = entry.contentRect;
      setContentBoxSize({ width: cr.width, height: cr.height });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const sessionStartedRef = useRef(false);
  const [startSession] = useStartSessionMutation();
  const [sessionStartError, setSessionStartError] = useState<string | null>(null);
  const [submitRound] = useSubmitRoundMutation();
  const [completeSession] = useCompleteSessionMutation();

  const [sessionId, setSessionId] = useState<string | null>(effectiveSessionId);
  const [phase, setPhase] = useState<Phase>("idle");
  const [sequenceLength, setSequenceLength] = useState(
    effectiveGame ? Math.max(MIN_SEQUENCE, Math.min(MAX_SEQUENCE, effectiveGame.difficulty)) : INITIAL_SEQUENCE_LENGTH
  );
  const [, setLastFiveCorrect] = useState<boolean[]>([]);
  const [sequence, setSequence] = useState<number[]>([]);
  const [userInput, setUserInput] = useState<number[]>([]);
  const [showIndex, setShowIndex] = useState(0);
  const [roundStartTime, setRoundStartTime] = useState<number>(0);
  const [roundCount, setRoundCount] = useState(0);
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);
  const [xpEarned, setXpEarned] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [sessionScore, setSessionScore] = useState(0);
  const [roundResults, setRoundResults] = useState<{ correct: boolean; reactionMs: number }[]>([]);
  const [lastRoundReactionMs, setLastRoundReactionMs] = useState(0);
  const [domainUpdatesSum, setDomainUpdatesSum] = useState<Record<string, number>>({});
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SessionSummaryStats | null>(null);
  const [muted, setMuted] = useState(false);
  const [tapTimestamps, setTapTimestamps] = useState<number[]>([]);
  const [, setReplayRequested] = useState(false);

  const shellDifficulty = effectiveGame
    ? Math.max(1, Math.min(10, effectiveGame.difficulty))
    : 1;

  const contentSeed = effectiveGame?.contentSeed;

  const startNewRound = useCallback(() => {
    const roundSeed = contentSeed ? `${contentSeed}-${roundCount}` : undefined;
    const seq = generateSequence(sequenceLength, roundSeed);
    setSequence(seq);
    setUserInput([]);
    setShowIndex(0);
    setPhase("showing");
    setRoundStartTime(0);
    setXpEarned(null);
    setFeedbackCorrect(false);
    setTapTimestamps([]);
    setReplayRequested(false);
  }, [sequenceLength, contentSeed, roundCount]);

  useEffect(() => {
    if (isGuided || isFreePlayFromLibrary) return;
    if (!selectedPatientId) {
      setSessionStartError(t("games.errors.onlyPatientCanPlay"));
      return;
    }
    if (sessionStartedRef.current) return;
    const now = Date.now();
    if (now - lastSessionStartAt < SESSION_START_DEDUPE_MS) return;
    lastSessionStartAt = now;
    sessionStartedRef.current = true;
    setSessionStartError(null);
    let mounted = true;
    startSession({ patientId: selectedPatientId })
      .unwrap()
      .then((res) => {
        if (mounted && res?.sessionId) {
          setSessionId(res.sessionId);
          setSessionStartTime(Date.now());
        }
      })
      .catch((err: { status?: number }) => {
        if (mounted && err?.status === 403) {
          setSessionStartError(t("games.errors.onlyPatientCanPlay"));
        }
      });
    return () => {
      mounted = false;
    };
  }, [isGuided, isFreePlayFromLibrary, selectedPatientId, startSession, t]);

  useEffect(() => {
    if ((isGuided && guidedSessionId) || isFreePlayFromLibrary) {
      setSessionStartTime(Date.now());
    }
  }, [isGuided, guidedSessionId, isFreePlayFromLibrary]);

  useEffect(() => {
    if (sessionStartTime == null || paused) return;
    const id = setInterval(() => {
      setTimerSeconds(Math.floor((Date.now() - sessionStartTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionStartTime, paused]);

  useEffect(() => {
    if (phase !== "showing" || showIndex >= sequence.length) return;
    const itemDelay = showIndex === 0 ? 0 : GAP_BETWEEN_MS;
    const t1 = window.setTimeout(() => {
      setShowIndex((i) => i + 1);
    }, itemDelay + SHOW_ITEM_MS);
    return () => clearTimeout(t1);
  }, [phase, showIndex, sequence.length]);

  useEffect(() => {
    if (phase !== "showing" || showIndex !== sequence.length) return;
    const t2 = window.setTimeout(() => {
      setPhase("recall");
      setRoundStartTime(Date.now());
    }, 500);
    return () => clearTimeout(t2);
  }, [phase, showIndex, sequence.length]);

  const handleCellClick = useCallback(
    (cellIndex: number) => {
      if (phase !== "recall") return;
      if (userInput.length >= sequence.length) return;
      const tapTime = Date.now();
      const next = [...userInput, cellIndex];
      const timestampsForRound = [...tapTimestamps, tapTime - roundStartTime];
      setUserInput(next);
      setTapTimestamps(timestampsForRound);
      if (next.length === sequence.length) {
        const correct =
          next.length === sequence.length &&
          next.every((v, i) => v === sequence[i]);
        const reactionTimeMs = Date.now() - roundStartTime;
        setLastRoundReactionMs(reactionTimeMs);
        setFeedbackCorrect(correct);
        setPhase("feedback");

        const accuracy = correct ? 1 : 0;
        setLastFiveCorrect((prev) => {
          const nextFive = [...prev, correct].slice(-ROUNDS_FOR_ACCURACY);
          if (nextFive.length >= ROUNDS_FOR_ACCURACY) {
            const acc = nextFive.filter(Boolean).length / nextFive.length;
            setSequenceLength((n) => {
              if (acc >= ACCURACY_UP_THRESHOLD)
                return Math.min(MAX_SEQUENCE, n + 1);
              if (acc < ACCURACY_DOWN_THRESHOLD)
                return Math.max(MIN_SEQUENCE, n - 1);
              return n;
            });
          }
          return nextFive;
        });

        setRoundResults((prev) => [...prev, { correct, reactionMs: reactionTimeMs }]);

        if (sessionId) {
          submitRound({
            sessionId,
            body: {
              gameId: GAME_IDS.sequenceRecall,
              roundNumber: roundCount + 1,
              difficulty: sequenceLength,
              accuracy,
              reactionTimeMs: reactionTimeMs,
              completionTimeMs: reactionTimeMs,
              errorCount: correct ? 0 : 1,
              rawData: {
                sequenceLength: sequence.length,
                sequence,
                userInput: next,
                correct,
                tapTimestamps: timestampsForRound,
                wrongTaps: correct ? 0 : 1,
              },
            },
          })
            .unwrap()
            .then((res) => {
              if (res?.xpEarned != null) {
                setXpEarned(res.xpEarned);
                setSessionScore((s) => s + res.xpEarned);
              }
              if (res?.domainUpdates && Object.keys(res.domainUpdates).length > 0) {
                setDomainUpdatesSum((prev) => {
                  const next = { ...prev };
                  for (const [k, v] of Object.entries(res.domainUpdates)) {
                    next[k] = (next[k] ?? 0) + v;
                  }
                  return next;
                });
              }
            })
            .catch(() => {});
        } else {
          const xp = correct ? 20 : 10;
          setXpEarned(xp);
          setSessionScore((s) => s + xp);
        }
      }
    },
    [phase, userInput, sequence, roundStartTime, sessionId, submitRound, sequenceLength, roundCount, tapTimestamps]
  );

  const handleNextRound = useCallback(() => {
    const nextRound = roundCount + 1;
    setRoundCount(nextRound);
    if (nextRound >= ROUNDS_PER_SESSION) {
      if (isGuided && onGuidedGameComplete) {
        onGuidedGameComplete();
        return;
      }
      if (isFreePlayFromLibrary) {
        // Fall through to completeSession + summary below
      }
      const allCorrect = [...roundResults.map((r) => r.correct), feedbackCorrect];
      const allReactions = [...roundResults.map((r) => r.reactionMs), lastRoundReactionMs];
      const totalRounds = allCorrect.length;

      if (sessionId) {
        completeSession({ sessionId })
          .unwrap()
          .then((res) => {
            setSummaryStats({
              accuracyPercent: (allCorrect.filter(Boolean).length / totalRounds) * 100,
              avgReactionSeconds:
                allReactions.reduce((a, b) => a + b, 0) / totalRounds / 1000,
              xpEarned: res?.totalXp ?? sessionScore,
              domainChanges: domainUpdatesSum,
            });
          })
          .catch(() => {
            setSummaryStats({
              accuracyPercent: (allCorrect.filter(Boolean).length / totalRounds) * 100,
              avgReactionSeconds:
                allReactions.reduce((a, b) => a + b, 0) / totalRounds / 1000,
              xpEarned: sessionScore,
              domainChanges: domainUpdatesSum,
            });
          });
      } else {
        setSummaryStats({
          accuracyPercent: (allCorrect.filter(Boolean).length / totalRounds) * 100,
          avgReactionSeconds:
            allReactions.reduce((a, b) => a + b, 0) / totalRounds / 1000,
          xpEarned: sessionScore,
          domainChanges: domainUpdatesSum,
        });
      }
      lastSessionStartAt = 0;
      setSessionId(null);
      setShowSessionSummary(true);
      return;
    }
    startNewRound();
  }, [
    roundCount,
    sessionId,
    isGuided,
    isFreePlayFromLibrary,
    onGuidedGameComplete,
    completeSession,
    startNewRound,
    roundResults,
    feedbackCorrect,
    lastRoundReactionMs,
    sessionScore,
    domainUpdatesSum,
  ]);

  const handleExit = useCallback(() => {
    if (sessionId) {
      completeSession({ sessionId }).catch(() => {});
    }
    lastSessionStartAt = 0;
    navigate(-1);
  }, [sessionId, completeSession, navigate]);

  const handleSummaryContinue = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const highlightedCell = useMemo(() => {
    if (phase !== "showing" || showIndex >= sequence.length) return null;
    return sequence[showIndex];
  }, [phase, showIndex, sequence]);

  const selectedSet = useMemo(
    () =>
      phase === "recall" || phase === "feedback"
        ? new Set(userInput)
        : new Set<number>(),
    [phase, userInput]
  );

  const correctSet = useMemo(
    () => (phase === "feedback" ? new Set(sequence) : new Set<number>()),
    [phase, sequence]
  );

  const feedbackText =
    phase === "showing"
      ? t("games.sequenceRecall.watch")
      : phase === "recall"
        ? t("games.sequenceRecall.repeat", { count: sequence.length })
        : phase === "feedback"
          ? feedbackCorrect
            ? t("games.sequenceRecall.feedbackCorrect")
            : t("games.sequenceRecall.feedbackIncorrect")
          : null;

  const progress =
    phase === "idle"
      ? 0
      : (roundCount + (phase === "feedback" ? 1 : 0)) / ROUNDS_PER_SESSION;

  // Keep reserved layout space constant across phases to avoid "jumping"
  // when different elements become visible.
  const dotsVisible =
    (phase === "recall" || phase === "feedback") && sequence.length > 0;
  // Must be high enough to fit the feedback/action content without spilling
  // into the shell footer (score) area.
  const reservedActionHeight = 175;

  const columnGapPx = 16; // must match `gap={2}` below (theme spacing 8px)
  const dotsHeightPx = 14;

  const cellSize = useMemo(() => {
    const viewportWidth =
      typeof window !== "undefined" ? window.innerWidth : contentBoxSize.width;
    // Keep a small horizontal gutter for shell/container padding on mobile.
    const viewportSafeWidth = Math.max(0, viewportWidth - 24);
    const availableWidth =
      contentBoxSize.width > 0
        ? Math.min(contentBoxSize.width, viewportSafeWidth)
        : viewportSafeWidth;
    const availableHeight = contentBoxSize.height;

    if (availableWidth <= 0 || availableHeight <= 0) {
      const fallbackFromWidth = Math.floor(
        (Math.max(0, viewportSafeWidth) - gridGap * (GRID_COLS - 1)) / GRID_COLS,
      );
      return Math.max(1, Math.min(fallbackCellSize, fallbackFromWidth));
    }

    // Always keep the grid within visible width.
    const maxCellWidth = Math.floor(
      (availableWidth - gridGap * (GRID_COLS - 1)) / GRID_COLS,
    );

    const availableHeightForGrid = Math.max(
      0,
      availableHeight -
        reservedActionHeight -
        dotsHeightPx -
        columnGapPx * 2,
    );

    const maxCellHeight = Math.floor(
      (availableHeightForGrid - gridGap * (GRID_ROWS - 1)) / GRID_ROWS,
    );

    // If height is constrained, still clamp by width so the grid never
    // overflows horizontally on smaller screens.
    const computed =
      maxCellHeight > 0
        ? Math.floor(Math.min(maxCellWidth, maxCellHeight))
        : Math.floor(maxCellWidth);

    // Resolution-aware clamp:
    // - prefer larger tap targets when space allows
    // - shrink on very small devices without overflowing width
    const preferredMinCell = 48;
    const hardMinCell = 32;
    const preferredSized = Math.max(preferredMinCell, computed);
    if (maxCellWidth <= hardMinCell) {
      return Math.max(1, maxCellWidth);
    }
    return Math.max(hardMinCell, Math.min(maxCellWidth, preferredSized));
  }, [
    contentBoxSize.width,
    contentBoxSize.height,
    gridGap,
    reservedActionHeight,
    dotsHeightPx,
    fallbackCellSize,
  ]);

  if (showSessionSummary && summaryStats) {
    return (
      <SessionSummary stats={summaryStats} onContinue={handleSummaryContinue} />
    );
  }

  return (
    <GameScreenShell
      gameName={t("games.sequenceRecall.title")}
      level={shellDifficulty}
      progress={progress}
      feedback={feedbackText}
      timerSeconds={sessionStartTime != null ? timerSeconds : null}
      score={sessionScore > 0 ? sessionScore : null}
      paused={paused}
      onBack={handleExit}
      onPause={() => setPaused((p) => !p)}
      gameIndex={isGuided ? guidedGameIndex : undefined}
      totalGames={isGuided ? guidedTotalGames : undefined}
      muted={muted}
      onMuteChange={setMuted}
    >
      {phase === "idle" && (
        <Box 
          textAlign="center" 
          py={4} 
          flex={1} 
          display="flex" 
          flexDirection="column" 
          justifyContent="center"
          >
          {sessionStartError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {sessionStartError}
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            {t("games.sequenceRecall.instructionsTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("games.sequenceRecall.instructions")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={startNewRound}
            sx={gameContinueButtonSx}
          >
            {t("games.sequenceRecall.startRound")}
          </Button>
        </Box>
      )}

      {(phase === "showing" || phase === "recall" || phase === "feedback") && (
        <Box
          ref={contentBoxRef}
          sx={{
            flex: 1,
            width: "100%",
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${GRID_COLS}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${GRID_ROWS}, minmax(0, 1fr))`,
              gap: `${gridGap}px`,
              width: `min(100%, calc(100vw - 24px), ${GRID_COLS * cellSize + gridGap * (GRID_COLS - 1)}px)`,
              aspectRatio: `${GRID_COLS} / ${GRID_ROWS}`,
              margin: "0 auto",
              flexShrink: 0,
            }}
          >
            {Array.from({ length: CELL_COUNT }, (_, i) => (
              <Box
                key={i}
                onClick={() => handleCellClick(i)}
                sx={{
                  width: "100%",
                  height: "100%",
                  aspectRatio: "1 / 1",
                  boxSizing: "border-box",
                  borderRadius: 2,
                  border: "2px solid",
                  borderColor:
                    highlightedCell === i
                      ? "primary.main"
                      : selectedSet.has(i)
                        ? theme.palette.primary.light
                        : "divider",
                  backgroundColor:
                    highlightedCell === i
                      ? "primary.main"
                      : phase === "feedback" &&
                          correctSet.has(i) &&
                          !feedbackCorrect
                        ? "action.selected"
                        : selectedSet.has(i)
                          ? "primary.light"
                          : "background.paper",
                  opacity:
                    phase === "feedback" &&
                    !correctSet.has(i) &&
                    !selectedSet.has(i)
                      ? 0.6
                      : 1,
                  cursor: phase === "recall" ? "pointer" : "default",
                  transition:
                    "background-color 0.2s, border-color 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            ))}
          </Box>

          <Box
            display="flex"
            justifyContent="center"
            gap={0.75}
            flexShrink={0}
            width="100%"
            sx={{
              height: dotsHeightPx,
              minHeight: dotsHeightPx,
              opacity: dotsVisible ? 1 : 0,
              pointerEvents: "none",
              alignItems: "center",
              overflow: "hidden",
            }}
            aria-hidden={!dotsVisible}
          >
            {Array.from({ length: sequence.length }, (_, i) => (
              <Box
                key={i}
                sx={{
                  width: dotsHeightPx,
                  height: dotsHeightPx,
                  borderRadius: "50%",
                  bgcolor:
                    i < userInput.length
                      ? "primary.main"
                      : "action.hover",
                  transition: "background-color 0.2s",
                }}
              />
            ))}
          </Box>

          {/* Action block placeholder to avoid jumping; kept smaller pre-feedback
              so the pad can maximize available space. */}
          <Box
            sx={{
              height: reservedActionHeight,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              flexShrink: 0,
              width: "100%",
            }}
          >
            {phase === "feedback" && (
              <>
                {xpEarned != null && (
                  <Typography variant="body2" color="primary.main">
                    +{xpEarned} XP
                  </Typography>
                )}
                {!feedbackCorrect && sequenceLength <= 4 && (
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      setPhase("showing");
                      setShowIndex(0);
                      setUserInput([]);
                      setTapTimestamps([]);
                      setReplayRequested(true);
                    }}
                    sx={gameSecondaryButtonSx}
                  >
                    {t("games.sequenceRecall.seeSequenceAgain")}
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleNextRound}
                  sx={{ ...gameContinueButtonSx, mb: 0 }}
                >
                  {roundCount + 1 >= ROUNDS_PER_SESSION
                    ? t("games.sequenceRecall.finishSession")
                    : t("games.sequenceRecall.nextRound")}
                </Button>
              </>
            )}
          </Box>
        </Box>
      )}
    </GameScreenShell>
  );
};

export default SequenceRecall;
